import { Link } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { ContentBlock } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';

import { BlockKindBadge, BlockScopeBadge, ContentStatusBadge } from './content-badges';

/**
 * Column model of the blocks table, for a store's list and the shared list alike; `meta` ties
 * columns to the list's sorts and filters. `storeId` is null on the shared list, whose links go
 * to /org/shared-blocks.
 */
export function useBlockColumns(storeId: string | null) {
  const { t } = useTranslation('content');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<ContentBlock>>>(
    () => [
      {
        id: 'name',
        header: t('columns.block'),
        cell: ({ row }) => (
          <div className="min-w-0">
            {row.original.scope === 'shared' || storeId === null ? (
              <Link
                to="/org/shared-blocks/$blockId"
                params={{ blockId: row.original.id }}
                className="block truncate font-medium hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {row.original.name}
              </Link>
            ) : (
              <Link
                to="/s/$storeId/content/blocks/$blockId"
                params={{ storeId, blockId: row.original.id }}
                className="block truncate font-medium hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {row.original.name}
              </Link>
            )}
            <code className="text-muted-foreground block truncate text-xs">{row.original.key}</code>
          </div>
        ),
        meta: { label: t('columns.block'), sortField: 'name', lockVisibility: true },
      },
      {
        id: 'kind',
        header: t('columns.kind'),
        cell: ({ row }) => (
          <span className="flex items-center gap-1">
            <BlockKindBadge kind={row.original.kind} />
            <BlockScopeBadge scope={row.original.scope} />
          </span>
        ),
        meta: { label: t('columns.kind'), filterKey: 'kind' },
      },
      {
        id: 'status',
        header: t('columns.status'),
        cell: ({ row }) => <ContentStatusBadge status={row.original.status} />,
        meta: { label: t('columns.status'), filterKey: 'status' },
      },
      {
        id: 'sections',
        header: t('columns.sections'),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {t('columns.sectionCount', { count: row.original.sectionCount })}
          </span>
        ),
        meta: { label: t('columns.sections') },
      },
      {
        id: 'warnings',
        header: t('columns.warnings'),
        cell: ({ row }) =>
          row.original.warnings.length > 0 ? (
            <span
              className="text-warning-foreground dark:text-warning inline-flex items-center gap-1 text-xs"
              title={t('warnings.title', { count: row.original.warnings.length })}
            >
              <AlertTriangle className="size-3.5" aria-hidden />
              {row.original.warnings.length}
            </span>
          ) : null,
        meta: { label: t('columns.warnings'), filterKey: 'hasWarnings' },
      },
      {
        id: 'updatedAt',
        header: t('columns.updated'),
        cell: ({ row }) => (
          <span className="text-muted-foreground" title={format.utc(row.original.updatedAt)}>
            {format.relative(row.original.updatedAt)}
          </span>
        ),
        meta: { label: t('columns.updated'), sortField: 'updatedAt' },
      },
    ],
    [format, storeId, t],
  );
}
